<template>
  <form
    class="flex flex-col items-center"
    novalidate="true"
    @submit.prevent="sendForm"
  >
    <InputField
      v-if="isProfile"
      id="oldPassword"
      v-model="form.oldPassword"
      type="password"
      left-icon-class="icon-padlock"
      :label="$t('old_password')"
      :has-error="v$.oldPassword.$invalid && v$.oldPassword.$anyDirty"
    />
    <InputField
      id="newPassword"
      v-model="form.newPassword"
      type="password"
      left-icon-class="icon-padlock"
      :label="$t('new_password')"
      :has-error="v$.newPassword.$invalid && v$.newPassword.$anyDirty"
    />

    <ul class="simple-list mb-24 pl-16">
      <li
        class="simple-list__item"
        :class="{
          'simple-list__item--success':
            !v$.newPassword.required.$invalid &&
            !v$.newPassword.minLength.$invalid &&
            !v$.newPassword.maxLength.$invalid
        }"
      >
        {{
          $t('password_length_must_be', {
            min: v$.newPassword.minLength.$params.min,
            max: v$.newPassword.maxLength.$params.max
          })
        }}
      </li>
      <li
        v-if="Object.hasOwn(v$.newPassword, 'has_numbers')"
        class="simple-list__item"
        :class="{
          'simple-list__item--success':
            !v$.newPassword.required.$invalid &&
            !v$.newPassword.has_numbers.$invalid
        }"
      >
        {{ $t('password_at_least_one_number') }}
      </li>
      <li
        v-if="Object.hasOwn(v$.newPassword, 'has_uppers')"
        class="simple-list__item"
        :class="{
          'simple-list__item--success':
            !v$.newPassword.required.$invalid &&
            !v$.newPassword.has_uppers.$invalid
        }"
      >
        {{ $t('password_at_least_one_capital_letter') }}
      </li>
    </ul>

    <InputField
      id="repeatNewPassword"
      v-model="form.repeatNewPassword"
      type="password"
      left-icon-class="icon-padlock"
      :label="$t('repeat_password')"
      :has-error="
        v$.repeatNewPassword.$invalid && v$.repeatNewPassword.$anyDirty
      "
    />

    <button
      class="btn btn--purple"
      :class="{ 'btn--loading': loading }"
      :disabled="v$.$invalid"
      type="submit"
    >
      <template v-if="loading">
        <LoadingDots />
      </template>
      <template v-else>
        {{ $t('set_new_password') }}
        <span class="icon-simple-arrow-right btn__icon"></span>
      </template>
    </button>
  </form>
</template>

<script setup>
import { computed, reactive } from 'vue'
import { InputField } from '@snowshoe-frontend/form-fields-components'
import { LoadingDots } from '@snowshoe-frontend/shared-components'
import useVuelidate from '@vuelidate/core'
import changePasswordSchema from '@/openapi-schemas/change_password.json'
import {
  schemaToValidators,
  removeValidationRule,
  addValidationsRule
} from '@snowshoe-frontend/utils-component'

const emit = defineEmits(['sendForm'])

const props = defineProps({
  loading: {
    type: Boolean,
    default: false
  },
  isProfile: {
    type: Boolean,
    default: false
  }
})

const form = reactive({
  oldPassword: '',
  newPassword: '',
  repeatNewPassword: ''
})

const additionalRule = {
  repeatNewPassword: {
    sameAs: function () {
      return form.newPassword === form.repeatNewPassword
    }
  }
}

const validations = addValidationsRule(
  schemaToValidators(changePasswordSchema),
  additionalRule
)

const rules = computed(() => {
  return props.isProfile
    ? {
        ...validations
      }
    : { ...removeValidationRule(validations, 'oldPassword') }
})

const v$ = useVuelidate(rules, form, { $autoDirty: true })

function sendForm() {
  emit('sendForm', form)
}
</script>
